import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const cardStyle = {
  background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(24px)',
  border: '1px solid #E2E8F0', borderRadius: 20,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: 32,
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#0F172A', marginBottom: 6, fontFamily: 'Poppins, sans-serif' };

const inputStyle = {
  width: '100%', padding: '12px 16px', borderRadius: 12,
  border: '2px solid #E2E8F0', fontSize: 15, fontFamily: 'Poppins, sans-serif',
  background: 'rgba(255,255,255,0.7)', color: '#0F172A', outline: 'none',
  transition: 'border-color 0.2s', boxSizing: 'border-box',
};

const btnPrimary = {
  padding: '14px 28px', borderRadius: 14,
  background: '#c9a84c', color: 'white',
  fontSize: 15, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
  boxShadow: '0 2px 8px rgba(201,168,76,0.3)',
};

const btnTertiary = {
  padding: '14px 28px', borderRadius: 14,
  background: 'transparent', color: '#0F172A',
  fontSize: 15, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
  border: '2px solid #E2E8F0', cursor: 'pointer', transition: 'all 0.15s',
};

export default function SignupForm({ onSwitch }) {
  const { signUp } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('');
  const [plan, setPlan] = useState(''); // 'inventor' | 'studio' | 'company'
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      if (!email || !password || !displayName) { setError('All fields are required'); return; }
      if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
      setError(''); setStep(2); return;
    }
    if (!role) { setError('Please select an account type'); return; }
    if (role === 'company' && !companyName.trim()) { setError('Company name is required'); return; }
    setError(''); setLoading(true);
    try {
      await signUp(email, password, role, displayName, role === 'company' ? companyName : null);

      // If studio plan selected, redirect to Stripe checkout after signup
      if (plan === 'studio') {
        // Note: user needs to verify email first, then Stripe checkout happens after login
        setSuccess(true);
      } else {
        setSuccess(true);
      }
    }
    catch (err) { setError(err.message); }
    setLoading(false);
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ ...cardStyle, textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>
              ✉️
            </div>
            <h2 style={{ fontFamily: '"EB Garamond", Georgia, serif', fontSize: 28, fontWeight: 700, color: '#0F172A', margin: '0 0 8px' }}>Check your email</h2>
            <p style={{ fontSize: 14, color: '#64748B', fontFamily: 'Poppins, sans-serif', lineHeight: 1.6 }}>
              We sent a verification link to <strong style={{ color: '#0F172A' }}>{email}</strong>. Click it to activate your account.
            </p>
            <button onClick={onSwitch} style={{ ...btnTertiary, marginTop: 24 }}>Back to Sign In</button>
          </div>
        </div>
      </div>
    );
  }

  const roleOption = (value, label, desc) => (
    <button
      type="button"
      onClick={() => { setRole(value); setPlan(''); }}
      style={{
        padding: 16, borderRadius: 14, textAlign: 'left', cursor: 'pointer', width: '100%',
        border: `2px solid ${role === value && !plan ? '#c9a84c' : '#E2E8F0'}`,
        background: role === value && !plan ? 'rgba(201,168,76,0.05)' : 'transparent',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 14, fontFamily: 'Poppins, sans-serif' }}>{label}</div>
      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2, fontFamily: 'Poppins, sans-serif' }}>{desc}</div>
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <img src="/logo-playventr.png" alt="PlayVentr" style={{ height: 48, margin: '0 auto 12px', display: 'block' }} />
          <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 6, fontFamily: 'Poppins, sans-serif' }}>Create your account</div>
        </div>

        {/* Card */}
        <div style={cardStyle}>
          {error && (
            <div style={{ marginBottom: 20, padding: 12, borderRadius: 12, background: '#FEF2F2', border: '1px solid #FECACA', color: '#F97066', fontSize: 13, fontFamily: 'Poppins, sans-serif' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {step === 1 && (
              <>
                <h2 style={{ fontFamily: '"EB Garamond", Georgia, serif', fontSize: 28, fontWeight: 700, color: '#0F172A', margin: '0 0 24px' }}>Account Details</h2>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Full Name</label>
                  <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} required style={inputStyle}
                    onFocus={e => e.target.style.borderColor = '#c9a84c'} onBlur={e => e.target.style.borderColor = '#E2E8F0'} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle}
                    onFocus={e => e.target.style.borderColor = '#c9a84c'} onBlur={e => e.target.style.borderColor = '#E2E8F0'} />
                </div>
                <div style={{ marginBottom: 28 }}>
                  <label style={labelStyle}>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="At least 6 characters" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = '#c9a84c'} onBlur={e => e.target.style.borderColor = '#E2E8F0'} />
                </div>
                <button type="submit" style={{ ...btnPrimary, width: '100%' }}>Continue</button>
              </>
            )}

            {step === 2 && (
              <>
                <h2 style={{ fontFamily: '"EB Garamond", Georgia, serif', fontSize: 28, fontWeight: 700, color: '#0F172A', margin: '0 0 8px' }}>I am a...</h2>
                <p style={{ fontSize: 13, color: '#64748B', fontFamily: 'Poppins, sans-serif', lineHeight: 1.6, marginBottom: 20 }}>
                  Choose the account type that best describes you.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {roleOption('inventor', 'Solo Inventor', 'Manage ideas, build pitches, and connect with companies')}
                  <button
                    type="button"
                    onClick={() => { setRole('inventor'); setPlan('studio'); }}
                    style={{
                      padding: 16, borderRadius: 14, textAlign: 'left', cursor: 'pointer', width: '100%',
                      border: `2px solid ${plan === 'studio' ? '#c9a84c' : '#E2E8F0'}`,
                      background: plan === 'studio' ? 'rgba(201,168,76,0.05)' : 'transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 14, fontFamily: 'Poppins, sans-serif' }}>Inventor Studio</div>
                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2, fontFamily: 'Poppins, sans-serif' }}>A team of inventors working together under one studio brand</div>
                  </button>
                  {roleOption('company', 'Toy Company', 'Discover inventors, review submissions, and attend pitch events')}
                </div>
                {role === 'company' && (
                  <div style={{ marginTop: 16 }}>
                    <label style={labelStyle}>Company Name</label>
                    <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} required style={inputStyle}
                      placeholder="e.g. The Fantastic Factory"
                      onFocus={e => e.target.style.borderColor = '#c9a84c'} onBlur={e => e.target.style.borderColor = '#E2E8F0'} />
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
                  <button type="button" onClick={() => setStep(1)} style={{ ...btnTertiary, flex: 1 }}>Back</button>
                  <button type="submit" disabled={loading || !role}
                    style={{ ...btnPrimary, flex: 1, opacity: loading || !role ? 0.5 : 1, cursor: loading || !role ? 'not-allowed' : 'pointer' }}>
                    {loading ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              </>
            )}
          </form>

          {step === 1 && (
            <p style={{ marginTop: 24, textAlign: 'center', fontSize: 13, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>
              Already have an account?{' '}
              <button onClick={onSwitch} style={{ background: 'none', border: 'none', color: '#c9a84c', fontWeight: 600, cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: 13 }}>
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
